import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  User,
  Phone,
  Eye,
  CheckCircle,
  Star,
  X,
  FileText
} from 'lucide-react';
import { PageTransition } from '../ui/PageTransition';
import { PageTitle } from '../ui/PageTitle';
import { contractService } from '../../services/contractService';
import { Contract } from '../../types/contract';

const Calendario: React.FC = () => {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'day'>('month');

  useEffect(() => {
    loadContracts();
  }, []);

  // Função utilitária para parsing robusto de datas
  const parseContractDate = (dataFesta: string | undefined): Date | null => {
    if (!dataFesta || dataFesta.trim() === '') return null;
    
    try {
      let contractDate;
      if (typeof dataFesta === 'string') {
        if (dataFesta.includes('/')) {
          // Formato brasileiro DD/MM/YYYY
          const [day, month, year] = dataFesta.split('/');
          contractDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else if (dataFesta.includes('-')) {
          // Formato ISO YYYY-MM-DD - criar data local para evitar problemas de fuso horário
          const [year, month, day] = dataFesta.split('-');
          contractDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          // Outros formatos
          contractDate = new Date(dataFesta);
        }
      } else {
        contractDate = new Date(dataFesta);
      }
      
      // Verificar se a data é válida
      if (isNaN(contractDate.getTime())) {
        return null;
      }
      
      return contractDate;
    } catch (error) {
      console.error('❌ Erro ao processar data:', dataFesta, error);
      return null;
    }
  };

  const loadContracts = async () => {
    try {
      setLoading(true);
      const data = await contractService.getContracts();
      setContracts(data);
    } catch (error) {
      console.error('Erro ao carregar contratos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Adicionar dias do mês anterior para completar a primeira semana
    for (let i = 0; i < startingDay; i++) {
      const prevDate = new Date(year, month, -startingDay + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false, contracts: [] });
    }
    
    // Adicionar dias do mês atual
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = new Date(year, month, i);
      const dayContracts = contracts.filter(contract => {
        const contractDate = parseContractDate(contract.dataFesta);
        if (!contractDate) return false;
        
        return contractDate.toDateString() === currentDate.toDateString();
      });
      days.push({ date: currentDate, isCurrentMonth: true, contracts: dayContracts });
    }
    
    // Adicionar dias do próximo mês para completar a última semana
    const remainingDays = 42 - days.length; // 6 semanas * 7 dias
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = new Date(year, month + 1, i);
      days.push({ date: nextDate, isCurrentMonth: false, contracts: [] });
    }
    
    return days;
  };

  const getWeekDays = (startDate: Date) => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const dayContracts = contracts.filter(contract => {
        const contractDate = parseContractDate(contract.dataFesta);
        if (!contractDate) return false;
        return contractDate.toDateString() === date.toDateString();
      });
      days.push({ date, contracts: dayContracts });
    }
    return days;
  };

  const getCurrentWeekStart = (date: Date) => {
    const current = new Date(date);
    const day = current.getDay();
    const diff = current.getDate() - day;
    return new Date(current.setDate(diff));
  };

  const getCurrentDay = (date: Date) => {
    const dayContracts = contracts.filter(contract => {
      const contractDate = parseContractDate(contract.dataFesta);
      if (!contractDate) return false;
      return contractDate.toDateString() === date.toDateString();
    });
    return { date, contracts: dayContracts };
  };

  const getWeekDayNames = () => {
    return ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  };

  const getMonthName = (date: Date) => {
    return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToPreviousWeek = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() - 7);
      return newDate;
    });
  };

  const goToNextWeek = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + 7);
      return newDate;
    });
  };

  const goToPreviousDay = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() - 1);
      return newDate;
    });
  };

  const goToNextDay = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + 1);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const getStatusColor = (status: Contract['status']) => {
    switch (status) {
      case 'confirmado': return 'bg-emerald-500';
      case 'pendente': return 'bg-amber-500';
      case 'orcamento': return 'bg-blue-500';
      case 'concluido': return 'bg-purple-500';
      case 'cancelado': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getStatusLabel = (status: Contract['status']) => {
    switch (status) {
      case 'confirmado': return 'Confirmado';
      case 'pendente': return 'Pendente';
      case 'orcamento': return 'Orçamento';
      case 'concluido': return 'Concluído';
      case 'cancelado': return 'Cancelado';
      default: return 'Pendente';
    }
  };

  const getStatusBorderColor = (status: Contract['status']) => {
    switch (status) {
      case 'confirmado': return 'border-emerald-200';
      case 'pendente': return 'border-amber-200';
      case 'orcamento': return 'border-blue-200';
      case 'concluido': return 'border-purple-200';
      case 'cancelado': return 'border-red-200';
      default: return 'border-slate-200';
    }
  };



  const getStatusBgColor = (status: Contract['status']) => {
    switch (status) {
      case 'confirmado': return 'bg-emerald-50';
      case 'pendente': return 'bg-amber-50';
      case 'orcamento': return 'bg-blue-50';
      case 'concluido': return 'bg-purple-50';
      case 'cancelado': return 'bg-red-50';
      default: return 'bg-slate-50';
    }
  };

  // Estatísticas do mês atual
  const getMonthStats = () => {
    const monthContracts = contracts.filter(c => {
      const contractDate = parseContractDate(c.dataFesta);
      if (!contractDate) return false;
      
      return contractDate.getMonth() === currentDate.getMonth() && 
             contractDate.getFullYear() === currentDate.getFullYear();
    });

    return {
      total: monthContracts.length,
      confirmados: monthContracts.filter(c => c.status === 'confirmado').length,
      pendentes: monthContracts.filter(c => c.status === 'pendente').length,
      orcamentos: monthContracts.filter(c => c.status === 'orcamento').length,
      concluidos: monthContracts.filter(c => c.status === 'concluido').length,
      cancelados: monthContracts.filter(c => c.status === 'cancelado').length,
    };
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Carregando calendário...</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  const monthStats = getMonthStats();

  return (
    <PageTransition>
      <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 overflow-x-hidden">
        {/* Header Principal */}
        <div className="mx-2 sm:mx-4 lg:mx-6 mt-2 mb-3">
          <PageTitle
            title="Calendário"
            subtitle="Visualize todos os eventos e contratos"
          >
            <div className="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30">
              <CalendarIcon className="w-6 h-6 text-white" />
            </div>
            
            <button
              onClick={goToToday}
              className="px-3 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 flex items-center gap-2 text-sm font-semibold border border-white/30 transition-all duration-200"
            >
              <CalendarIcon className="w-4 h-4" />
              Hoje
            </button>
          </PageTitle>
        </div>

                {/* Estatísticas do Mês */}
        <div className="mx-2 sm:mx-4 lg:mx-6 mb-2">
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600">Total</p>
                  <p className="text-base font-bold text-slate-900">{monthStats.total}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600">Confirmados</p>
                  <p className="text-base font-bold text-slate-900">{monthStats.confirmados}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600">Pendentes</p>
                  <p className="text-base font-bold text-slate-900">{monthStats.pendentes}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600">Orçamentos</p>
                  <p className="text-base font-bold text-slate-900">{monthStats.orcamentos}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Star className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600">Concluídos</p>
                  <p className="text-base font-bold text-slate-900">{monthStats.concluidos}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center">
                  <X className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-slate-600">Cancelados</p>
                  <p className="text-base font-bold text-slate-900">{monthStats.cancelados}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

                {/* Controles do Calendário */}
        <div className="mx-2 sm:mx-4 lg:mx-6 mb-2">
          <div className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              <button
                onClick={
                  viewMode === 'month' ? goToPreviousMonth :
                  viewMode === 'week' ? goToPreviousWeek :
                  goToPreviousDay
                }
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              <h2 className="text-xl font-bold text-slate-900 capitalize">
                {viewMode === 'month' ? getMonthName(currentDate) :
                 viewMode === 'week' ? `Semana de ${getCurrentWeekStart(currentDate).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })}` :
                 currentDate.toLocaleDateString('pt-BR', { 
                   weekday: 'long', 
                   year: 'numeric', 
                   month: 'long', 
                   day: 'numeric' 
                 })}
              </h2>
              
              <button
                onClick={
                  viewMode === 'month' ? goToNextMonth :
                  viewMode === 'week' ? goToNextWeek :
                  goToNextDay
                }
                className="p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('month')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  viewMode === 'month'
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-600 bg-white hover:bg-slate-50 border border-slate-200'
                }`}
              >
                Mês
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  viewMode === 'week'
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-600 bg-white hover:bg-slate-50 border border-slate-200'
                }`}
              >
                Semana
              </button>
              <button
                onClick={() => setViewMode('day')}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  viewMode === 'day'
                      ? 'bg-teal-600 text-white'
                      : 'text-slate-600 bg-white hover:bg-slate-50 border border-slate-200'
                }`}
              >
                Dia
              </button>
          </div>
        </div>

                    {/* Calendário */}
            {viewMode === 'month' && (
              <div>
                {/* Cabeçalho dos dias da semana */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {getWeekDayNames().map((day) => (
                    <div key={day} className="text-center py-0.5 text-xs font-medium text-slate-600">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Grade do calendário */}
                <div className="grid grid-cols-7 gap-1">
                  {getDaysInMonth(currentDate).map((day, index) => (
                    <div
                      key={index}
                      onClick={() => handleDateClick(day.date)}
                      className={`min-h-[60px] p-1 border border-slate-200 rounded-lg cursor-pointer transition-colors ${
                        day.isCurrentMonth 
                          ? 'bg-white hover:bg-slate-50' 
                          : 'bg-slate-50/50 text-slate-400'
                      } ${
                        selectedDate && day.date.toDateString() === selectedDate.toDateString()
                          ? 'ring-2 ring-teal-500 ring-opacity-50'
                          : ''
                      }`}
                    >
                      <div className="text-right mb-0.5">
                        <span className={`text-xs font-medium ${
                          day.isCurrentMonth ? 'text-slate-900' : 'text-slate-400'
                        }`}>
                          {day.date.getDate()}
                        </span>
                      </div>
                      
                      {/* Eventos do dia */}
                      <div className="space-y-0.5">
                        {day.contracts.slice(0, 1).map((contract) => (
                          <div
                            key={contract.id}
                            className={`text-xs p-0.5 rounded truncate ${getStatusColor(contract.status)} text-white`}
                            title={`${contract.contratante} - ${contract.nomeAniversariante} (${getStatusLabel(contract.status)})`}
                          >
                            {contract.contratante}
                          </div>
                        ))}
                        {day.contracts.length > 1 && (
                          <div className="text-xs text-slate-500 text-center">
                            +{day.contracts.length - 1} mais
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visualização de Semana */}
            {viewMode === 'week' && (
              <div>
                {/* Cabeçalho da semana */}
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {getWeekDayNames().map((dayName, index) => (
                    <div key={index} className="text-center py-1 text-xs font-medium text-slate-600 bg-slate-50 rounded-t-lg">
                      {dayName}
                    </div>
                  ))}
                </div>
                
                {/* Grade da semana */}
                <div className="grid grid-cols-7 gap-1">
                  {getWeekDays(getCurrentWeekStart(currentDate)).map((day, index) => (
                    <div
                      key={index}
                      onClick={() => handleDateClick(day.date)}
                      className={`min-h-[120px] p-2 border border-slate-200 rounded-lg cursor-pointer transition-colors ${
                        day.date.toDateString() === new Date().toDateString()
                          ? 'bg-teal-50 border-teal-300'
                          : 'bg-white hover:bg-slate-50'
                      }`}
                    >
                      <div className="text-xs font-medium text-slate-900 mb-1">
                        {day.date.getDate()}
                      </div>
                      
                      {/* Eventos do dia */}
                      <div className="space-y-1">
                        {day.contracts.slice(0, 3).map((contract, contractIndex) => (
                          <div
                            key={contract.id}
                            className={`text-xs p-1 rounded ${getStatusBgColor(contract.status)} ${getStatusBorderColor(contract.status)}`}
                          >
                            <div className="font-medium text-slate-900 truncate">
                              {contract.contratante}
                            </div>
                            <div className="text-slate-600 truncate">
                              {contract.horarioInicioFesta}
                            </div>
                          </div>
                        ))}
                        
                        {day.contracts.length > 3 && (
                          <div className="text-xs text-slate-500 text-center py-1">
                            +{day.contracts.length - 3} mais
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Visualização de Dia */}
            {viewMode === 'day' && (
              <div>
                {/* Cabeçalho do dia */}
                <div className="text-center py-2 mb-2 bg-slate-50 rounded-lg">
                  <div className="text-lg font-bold text-slate-900">
                    {currentDate.toLocaleDateString('pt-BR', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </div>
                </div>
                
                {/* Grade do dia */}
                <div className="min-h-[400px] p-4 border border-slate-200 rounded-lg bg-white">
                  {(() => {
                    const dayData = getCurrentDay(currentDate);
                    return dayData.contracts.length > 0 ? (
                      <div className="space-y-3">
                        {dayData.contracts.map((contract) => (
                          <div
                            key={contract.id}
                            className={`p-3 rounded-lg border-2 ${getStatusBorderColor(contract.status)} ${getStatusBgColor(contract.status)}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${getStatusColor(contract.status)}`}></div>
                                <span className="font-semibold text-slate-900">
                                  {contract.contratante}
                                </span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(contract.status)} text-white font-medium`}>
                                {getStatusLabel(contract.status)}
                              </span>
                            </div>
                            
                            <div className="space-y-1 text-sm text-slate-600">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-500" />
                                <span>{contract.nomeAniversariante} ({contract.idadeAniversariante} anos)</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-500" />
                                <span>{contract.horarioInicioFesta} - {contract.fimRecreacao}</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-slate-500" />
                                <span className="truncate">{contract.enderecoFesta || contract.endereco}</span>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-500" />
                                <span>{contract.telefone}</span>
                              </div>
                            </div>
                            
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <button 
                                onClick={() => {
                                  navigate(`/admin/contratos/${contract.id}`);
                                  setSelectedDate(null);
                                }}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                              >
                                <Eye className="w-4 h-4" />
                                Ver Evento
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                        <p className="text-lg font-medium text-slate-600">Nenhum evento para este dia</p>
                        <p className="text-sm text-slate-400 mt-1">Clique em outro dia ou mude a visualização</p>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}


            </div>
        </div>

        {/* Painel lateral com detalhes da data selecionada */}
        {selectedDate && createPortal(
          <div className="fixed inset-0 z-50 flex">
            {/* Overlay com animação de entrada */}
            <div 
              className="absolute inset-0 bg-black/30 backdrop-blur-sm animate-[fadeIn_0.15s_ease-out_forwards]"
              onClick={() => setSelectedDate(null)}
            />
            
            {/* Sidebar com animação de entrada */}
            <div className="ml-auto relative bg-white shadow-2xl border-l border-slate-200 w-96 h-full flex flex-col animate-[slideInFromRight_0.2s_ease-out_forwards]">
            {/* Header elegante */}
            <div className="relative bg-gradient-to-r from-teal-500 to-teal-600 text-white p-6">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-white/30 to-teal-400"></div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <CalendarIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Eventos do Dia</h2>
                    <p className="text-sm text-teal-100 opacity-90">
                      {selectedDate.toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedDate(null)}
                  className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all duration-200 hover:scale-105"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Conteúdo da Sidebar */}
            <div className="flex-1 overflow-y-auto">
              {contracts.filter(c => {
                const contractDate = parseContractDate(c.dataFesta);
                if (!contractDate) return false;
                return contractDate.toDateString() === selectedDate.toDateString();
              }).length === 0 ? (
                // Estado vazio elegante
                <div className="p-8 text-center">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl mx-auto flex items-center justify-center">
                      <CalendarIcon className="w-10 h-10 text-slate-400" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="text-amber-600 text-xs">!</span>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">
                    Nenhum evento para este dia
                  </h3>
                  <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                    Este dia não possui eventos agendados. Clique em outro dia para visualizar os eventos.
                  </p>
                </div>
              ) : (
                // Lista de eventos
                <div className="p-6 space-y-4">
                  {contracts
                    .filter(contract => {
                      const contractDate = parseContractDate(contract.dataFesta);
                      if (!contractDate) return false;
                      return contractDate.toDateString() === selectedDate.toDateString();
                    })
                    .map(contract => (
                      <div 
                        key={contract.id} 
                        className={`rounded-xl p-4 border-2 ${getStatusBorderColor(contract.status)} ${getStatusBgColor(contract.status)} hover:shadow-md transition-all duration-200`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${getStatusColor(contract.status)}`}></div>
                            <span className="text-base font-semibold text-slate-900">
                              {contract.contratante}
                            </span>
                          </div>
                          <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(contract.status)} text-white font-medium`}>
                            {getStatusLabel(contract.status)}
                          </span>
                        </div>
                        
                        <div className="space-y-2 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-500" />
                            <span className="font-medium">{contract.nomeAniversariante} ({contract.idadeAniversariante} anos)</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-slate-500" />
                            <span>{contract.horarioInicioFesta} - {contract.fimRecreacao}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-500" />
                            <span className="truncate">{contract.enderecoFesta || contract.endereco}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 text-slate-500" />
                            <span>{contract.telefone}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <button 
                            onClick={() => {
                              navigate(`/admin/contratos/${contract.id}`);
                              setSelectedDate(null);
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all duration-200 font-medium shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
                          >
                            <Eye className="w-4 h-4" />
                            Ver Evento
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      </div>
    </PageTransition>
  );
};

export default Calendario;



